export type CookieMonitorConfig = {
	checkInterval?: number;
	useModernAPI?: boolean;
	onComplete?: () => void;
}

/**
 * Класс для мониторинга изменений cookie в браузере.
 * Поддерживает современный API `cookieStore` (в безопасном контексте) и резервный режим опроса.
 */
export class CookieMonitor {
	/**
	 * Создает экземпляр CookieMonitor.
	 *
	 * @param {Object} config - Конфигурация мониторинга.
	 * @param {number} [config.checkInterval=1000] - Интервал проверки cookie в миллисекундах (для резервного режима).
	 * @param {boolean} [config.useModernAPI=true] - Использовать ли современный API cookieStore, если доступен.
	 * @param {Function} [config.onCookieChange] - Колбэк, вызываемый при изменении cookie.
	 *
	 * @example
	 * const monitor = new CookieMonitor({
	 *   checkInterval: 500,
	 *   onCookieChange: (change) => console.log(change)
	 * });
	 */
	constructor(config :CookieMonitorConfig = {}) {
		this.config = {
			checkInterval: 1000,
			useModernAPI: true,
			onCookieChange: () => {},
			...config
		};

		this.knownCookies = new Set(this.getAllCookieNames());
		this.checkInterval = null;
		this.cookieStoreHandler = null;

		this.isModernAPIAvailable = window.isSecureContext && typeof window.cookieStore !== 'undefined';
	}

	/**
	 * Запускает мониторинг изменений cookie.
	 * Автоматически выбирает между современным API и резервным режимом.
	 */
	startMonitoring() {
		if (this.isModernAPIAvailable && this.config.useModernAPI) {
			this.startModernMonitoring();
		} else {
			this.startLegacyMonitoring();
		}
	}

	/**
	 * Останавливает текущий процесс мониторинга.
	 */
	stopMonitoring() {
		if (this.isModernAPIAvailable && this.cookieStoreHandler) {
			this.stopModernMonitoring();
		} else {
			this.stopLegacyMonitoring();
		}
	}

	/**
	 * Запускает мониторинг с использованием современного API cookieStore.
	 * @private
	 */
	startModernMonitoring() {
		if (!this.isModernAPIAvailable) return;

		this.cookieStoreHandler = (event) => {
			event.changed.forEach(({ name }) => {
				const currentValue = this.getCookieValue(name);
				this.config.onCookieChange({
					name,
					value: currentValue,
					action: 'change',
					timestamp: new Date()
				});

				this.knownCookies.add(name);
			});

			event.deleted.forEach(({ name }) => {
				this.config.onCookieChange({
					name,
					value: null,
					action: 'delete',
					timestamp: new Date()
				});

				this.knownCookies.delete(name);
			});
		};

		window.cookieStore.addEventListener('change', this.cookieStoreHandler);
	}

	/**
	 * Останавливает мониторинг через современный API.
	 * @private
	 */
	stopModernMonitoring() {
		if (this.cookieStoreHandler) {
			window.cookieStore.removeEventListener('change', this.cookieStoreHandler);
			this.cookieStoreHandler = null;
		}
	}

	/**
	 * Запускает резервный режим мониторинга через периодическую проверку document.cookie.
	 * @private
	 */
	startLegacyMonitoring() {
		this.checkInterval = setInterval(() => {
			this.checkCookieChanges();
		}, this.config.checkInterval);
	}

	/**
	 * Останавливает резервный режим мониторинга.
	 * @private
	 */
	stopLegacyMonitoring() {
		if (this.checkInterval) {
			clearInterval(this.checkInterval);
			this.checkInterval = null;
		}
	}

	/**
	 * Проверяет изменения cookie в резервном режиме.
	 * Вызывает колбэк для новых и удалённых cookie.
	 * @private
	 */
	checkCookieChanges() {
		const currentCookies = this.getAllCookieNames();
		const newCookies = currentCookies.filter(name => !this.knownCookies.has(name));
		const deletedCookies = Array.from(this.knownCookies).filter(name => !currentCookies.includes(name));

		newCookies.forEach(cookieName => {
			const value = this.getCookieValue(cookieName);
			this.knownCookies.add(cookieName);

			this.config.onCookieChange({
				name: cookieName,
				value: value,
				action: 'change',
				timestamp: new Date()
			});
		});

		deletedCookies.forEach(cookieName => {
			this.knownCookies.delete(cookieName);

			this.config.onCookieChange({
				name: cookieName,
				value: null,
				action: 'delete',
				timestamp: new Date()
			});
		});
	}

	/**
	 * Возвращает список имён всех доступных cookie.
	 * @returns {string[]} Массив имён cookie.
	 */
	getAllCookieNames() {
		return document.cookie.split('; ')
			.map(cookie => cookie.split('=')[0])
			.filter(Boolean);
	}

	/**
	 * Получает значение cookie по имени.
	 * Использует BX.getCookie, если доступен, иначе парсит document.cookie.
	 *
	 * @param {string} name - Имя cookie.
	 * @returns {string|null} Значение cookie или null, если не найдено.
	 */
	getCookieValue(name) {
		if (typeof window.BX !== 'undefined' && window.BX.getCookie) {
			return window.BX.getCookie(name);
		}

		const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
		return match ? decodeURIComponent(match[2]) : null;
	}

	/**
	 * Возвращает все cookie в виде объекта.
	 * @returns {Object} Объект с парами имя-значение всех cookie.
	 */
	getAllCookies() {
		const cookies = {};
		document.cookie.split('; ').forEach(cookie => {
			const [name, value] = cookie.split('=');
			if (name) {
				cookies[name] = decodeURIComponent(value);
			}
		});
		return cookies;
	}

	/**
	 * Проверяет, поддерживается ли мониторинг в текущем окружении.
	 * @returns {boolean} true, если доступен хотя бы один метод (современный или резервный).
	 */
	isSupported() {
		return this.isModernAPIAvailable || true;
	}

	/**
	 * Возвращает текущий режим мониторинга.
	 * @returns {'modern'|'legacy'} Текущий режим.
	 */
	getMode() {
		return this.isModernAPIAvailable && this.config.useModernAPI ? 'modern' : 'legacy';
	}

	/**
	 * Обновляет конфигурацию мониторинга.
	 * При изменении интервала или режима перезапускает мониторинг.
	 *
	 * @param {Object} newConfig - Новые параметры конфигурации.
	 */
	updateConfig(newConfig) {
		this.config = { ...this.config, ...newConfig };

		if (newConfig.checkInterval || newConfig.useModernAPI !== undefined) {
			const wasRunning = this.checkInterval || this.cookieStoreHandler;
			if (wasRunning) {
				this.stopMonitoring();
				this.startMonitoring();
			}
		}
	}

	/**
	 * Полностью останавливает мониторинг и очищает внутреннее состояние.
	 */
	destroy() {
		this.stopMonitoring();
		this.knownCookies.clear();
	}
}